///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The personal library, opened from the chevron beside the bookmark
/// button: Saved Views, My Places, Story Tours (when authoring is on) and
/// Recent, as tabs in one glass popover. Everything here is the user's own
/// content - the set that syncs with the account - which is why it lives
/// beside Save / Share rather than in the Layers catalog.
///

import { MyPlacesList } from "@/components/controls/library/MyPlacesList"
import { RecentsList } from "@/components/controls/library/RecentsList"
import { SavedViewsList } from "@/components/controls/library/SavedViewsList"
import { StoriesList } from "@/components/controls/library/StoriesList"
import { useRecentItems } from "@/components/controls/library/useRecentItems"
import { openViewsBrowser } from "@/components/features/views-browser/ViewsBrowserFeature"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { setLibraryTab, type LibraryTab } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { cStoryAuthoringEnabled } from "@/utils/featureGates"
import { cn } from "@/utils/utils"
import { Bookmark, BookOpenText, ChevronDown, History, MapPin, Maximize2 } from "lucide-react"
import { useCallback, useState } from "react"
import type { ReactNode } from "react"

interface TabDefinition {
	id: LibraryTab
	label: string
	icon: ReactNode
	/** Active-tab accent, matching each list's own sort-pill colour */
	accent: string
}

const cTabs: TabDefinition[] = [
	{ id: "views", label: "Views", icon: <Bookmark className="w-3.5 h-3.5" />, accent: "bg-cyan-500/20 text-cyan-300 border-cyan-500/60" },
	{ id: "places", label: "Places", icon: <MapPin className="w-3.5 h-3.5" />, accent: "bg-rose-500/20 text-rose-300 border-rose-500/60" },
	{ id: "stories", label: "Stories", icon: <BookOpenText className="w-3.5 h-3.5" />, accent: "bg-teal-500/20 text-teal-300 border-teal-500/60" },
	{ id: "recent", label: "Recent", icon: <History className="w-3.5 h-3.5" />, accent: "bg-amber-500/20 text-amber-300 border-amber-500/60" },
]

interface ViewsMenuProps {
	/** The chevron half of the bookmark split button */
	triggerClassName: string
}

export function ViewsMenu({ triggerClassName }: ViewsMenuProps) {
	const dispatch = useAppDispatch()
	const [open, setOpen] = useState(false)
	const tab = useAppSelector(state => state.dialog.libraryTab)
	const datasetWindows = useAppSelector(state => state.dialog.datasetWindows)
	const viewCount = useAppSelector(state => Object.keys(state.savedViews.views).length)
	const placeCount = useAppSelector(state => Object.keys(state.placemarks.placemarks).length)
	const storyCount = useAppSelector(state => Object.keys(state.stories.stories).length)
	const recents = useRecentItems()

	const counts: Record<LibraryTab, number> = {
		views: viewCount,
		places: placeCount,
		stories: storyCount,
		recent: recents.items.length,
	}

	const tabs = cTabs.filter(t => t.id !== "stories" || cStoryAuthoringEnabled)
	// Stories can be hidden by the feature gate after it was the active tab
	const activeTab = tabs.some(t => t.id === tab) ? tab : "views"

	const close = useCallback(() => setOpen(false), [])

	const handleRunRecent = useCallback((cmd: Parameters<typeof recents.run>[0]) => {
		recents.run(cmd)
		close()
	}, [recents, close])

	const handleOpenWindow = () => {
		openViewsBrowser(dispatch, datasetWindows)
		close()
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className={cn(triggerClassName, open && "bg-white/10 text-white")}
					title="Saved views, places and recent"
					aria-label="Open saved views and places"
					aria-expanded={open}
				>
					<ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-200", open && "rotate-180")} />
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				sideOffset={8}
				collisionPadding={8}
				// Above the dataset windows (z ~1000+) so the menu never
				// disappears behind an open browser
				className={cn(
					"z-[1100] w-[300px] max-h-[min(70vh,480px)] flex flex-col gap-1.5 p-2 text-white",
					"bg-black/40 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl"
				)}
				data-views-menu
			>
				<div className="flex items-center gap-1 flex-shrink-0" role="tablist" aria-label="Library">
					{tabs.map(definition => {
						const selected = definition.id === activeTab
						return (
							<button
								key={definition.id}
								role="tab"
								aria-selected={selected}
								onClick={() => dispatch(setLibraryTab(definition.id))}
								className={cn(
									"flex-1 flex items-center justify-center gap-1 h-7 px-1.5 text-[11px] rounded-full border",
									"transition-colors duration-200",
									"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
									selected
										? definition.accent
										: "bg-white/5 text-white/50 border-transparent hover:text-white hover:bg-white/10"
								)}
							>
								{definition.icon}
								<span>{definition.label}</span>
								{counts[definition.id] > 0 && (
									<span className={cn("tabular-nums", selected ? "opacity-80" : "text-white/35")}>
										{counts[definition.id]}
									</span>
								)}
							</button>
						)
					})}
					{activeTab === "views" && (
						<button
							onClick={handleOpenWindow}
							className={cn(
								"w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0",
								"text-white/40 hover:text-white hover:bg-white/10 transition-colors duration-200",
								"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
							)}
							title="Open saved views in a window"
							aria-label="Open saved views in a window"
						>
							<Maximize2 className="w-3.5 h-3.5" />
						</button>
					)}
				</div>

				<div className="flex-1 min-h-0 flex flex-col" role="tabpanel">
					{activeTab === "views" && <SavedViewsList onDone={close} />}
					{activeTab === "places" && <MyPlacesList onDone={close} />}
					{activeTab === "stories" && <StoriesList onDone={close} />}
					{activeTab === "recent" && <RecentsList items={recents.items} onRun={handleRunRecent} />}
				</div>
			</PopoverContent>
		</Popover>
	)
}
